<template>
    <div id="user-bank-account-management" class="container-fluid px-5 py-5">
        <div class="vx-row same-h">
            <!-- header  -->
            <div class="vx-col w-full mb-6">
                <vx-card>
                    <h1 class="bold big-title m-0">Gestión de cuentas<span> CLABE</span>.</h1>
                </vx-card>
            </div>

            <!-- container -->
            <div class="vx-col sm:w-full md:w-5/12">
                <!-- BUSCADOR -->
                <vx-card class="mb-4">
                    <h5>Expediente</h5>
                    <h6 class="mb-4">Proporciona el ID del Proveedor, Solicitante o Inversionista.</h6>
                    <div class="vx-row">
                        <div class="vx-col sm:w-full mb-4">
                            <vs-alert v-if="errorMessage" color="danger" icon="warning">
                                {{ errorMessage }}
                            </vs-alert>
                        </div>
                        <div class="vx-col sm:w-full md:w-4/12">
                            <vs-select
                                v-model="userType"
                                class="w-full"
                                placeholder="Tipo usuario">
                                <vs-select-item
                                    v-for="option in options"
                                    :key="option.value"
                                    :value="option.value"
                                    :text="option.label">
                                </vs-select-item>
                            </vs-select>
                        </div>
                        <div class="vx-col sm:w-full md:w-4/12">
                            <vs-input
                                v-model="userId" 
                                class="w-full"
                                required
                                type="text"
                                maxlength="10"
                                placeholder="ID del usuario">
                            </vs-input>
                        </div>
                        <div class="vx-col sm:w-full md:w-2/12">
                            <vs-button :disabled="isConsultButtonDisabled" color="primary" @click="fetchUserBankAccounts">Consultar</vs-button>
                        </div>
                    </div>
                    <vs-divider></vs-divider>
                    <div v-if="isMounted" class="vx-col w-full">
                        <h5>Resultados de la búsqueda</h5>
                        <vs-list>
                            <vs-list-item title="Nombre/Razón social">{{ userInfo.name || "--"   }}</vs-list-item>
                            <vs-list-item title="Persona">{{ translatePersonalType(userInfo.person_type) || "--" }}</vs-list-item>
                            <vs-list-item title="RFC">{{ userInfo.rfc || "--" }}</vs-list-item>
                            <vs-list-item v-if="userInfo.curp" title="CURP">{{ userInfo.curp || "--" }}</vs-list-item>
                            <vs-list-item title="UID">{{ userInfo.user_id || "--" }}</vs-list-item>
                            <vs-list-item title="Email">{{ userInfo.email || "--" }}</vs-list-item>
                        </vs-list>
                    </div>
                    <div v-else class="vx-col w-full">
                        <h6 class="text-center">Sin resultados</h6>
                    </div>
                </vx-card>
            </div>
            <div class="vx-col sm:w-full md:w-7/12">
                <vx-card>
                    <h5>Información de la cuenta bancaria</h5>
                    <h6 class="mb-4">Proporciona la cuenta CLABE que corresponda a los datos del perfil.</h6>
                    <div class="vx-row">
                        <div class="vx-col sm:w-full mb-4">
                            <vs-alert v-if="errorMessageClabe" color="danger" icon="warning">
                                {{ errorMessageClabe }}
                            </vs-alert>
                        </div>
                        <div class="vx-col sm:w-full md:w-4/12">
                            <vs-input
                                v-model="clabe"
                                :disabled="!hasProfile"
                                class="w-full"
                                required
                                type="text"
                                maxlength="18"
                                placeholder="CLABE interbancaria">
                            </vs-input>
                        </div>
                        <div class="vx-col sm:w-full md:w-2/12">
                            <vs-button @click.stop="registerBankAccount" :disabled="!hasProfile" color="primary">Registrar</vs-button>
                        </div>
                        <vs-divider></vs-divider>
                        <div class="vx-col w-full">
                            <h5>Cuentas CLABE relacionadas al perfil</h5>
                            <h6 class="mb-4">Da clic en el ícono de estrella para marcar una cuenta verificada como la cuenta favorita del perfil.</h6>
                            <vs-table :data="userBankAccounts">
                                <template slot="thead">
                                    <vs-th class="bold">BANCO</vs-th>
                                    <vs-th class="bold">CLABE</vs-th>
                                    <vs-th class="bold">ESTADO</vs-th>
                                    <vs-th class="bold">¿ES FAVORITA?</vs-th>
                                    <vs-th class="bold">NOTAS</vs-th>
                                    <vs-th class="bold">ACCIONES</vs-th>
                                </template>
                                <template slot-scope="{data}">
                                    <tbody>
                                        <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                                            <vs-td>
                                                {{ tr.bank.short_name }}
                                            </vs-td>
                                            <vs-td>
                                                {{ tr.clabe }}
                                            </vs-td>
                                            <vs-td>
                                                {{ translateBankAccountStatus(tr.verified) }}
                                            </vs-td>
                                            <vs-td>
                                                {{ tr.is_default ? 'Sí' : 'No' }}
                                            </vs-td>
                                            <vs-td>
                                                {{ tr.stp_verification_replay || 'N/A' }}
                                            </vs-td>
                                            <vs-td>
                                                <div class="flex items-center flex-grow flex-no-wrap w-full">
                                                    <feather-icon
                                                        icon="StarIcon"
                                                        :svgClasses="getDefaultAccountIconClass(tr)"
                                                        @click.stop="setAsDefaultRequest(tr)" />
                                                </div>
                                            </vs-td>
                                        </vs-tr>
                                    </tbody>
                                </template>
                            </vs-table>
                        </div>
                    </div>
                </vx-card>
            </div>
        </div>
    </div>
</template>

<script>
    import projectStatusHelper from "@mixins/projectStatusesHelper.js"
    import { BankAccountStatus } from "@/models/BankAccountStatus";
    const options = [
        { label: 'Proveedor', value: 'supplier' },
        { label: 'Solicitante', value: 'applicant' },
        { label: 'Inversionista', value: 'investor' }
    ];
    export default {
        name: 'UserBankAccountManagement',
        mixins: [projectStatusHelper],
        data: () => ({
            options: options,
            userType: null,
            userId: null,
            clabe: null,
            userBankAccounts: [],
            userInfo: null,
            hasProfile: false,
            errorMessage: null,
            errorMessageClabe: null,
            isMounted: false,
            accountIdToSetAsDefault: null,
            klassBase: "h-5 w-5 stroke-current",
            klassFull: "h-5 w-5 stroke-current hover:text-primary cursor-pointer"
        }),
        computed:{
            isConsultButtonDisabled() {
                return this.userId === null || this.userType === null || this.userId === '' || this.userType === '';
            }
        },
        methods: {
            async fetchUserBankAccounts() {
                // Busca con el metodo post: /api/bank-accounts/user-info el detalle del expediente.
                // Si el resultado es exitoso, asigna a userBankAccounts el arreglo de cuentas bancarias.
                // Si el resultado es exitoso, asigna a hasProfile true.
                try {
                    this.showLoading(true, "Consultando información del usuario...");
                    this.errorMessage = null;
                    this.errorMessageClabe = null;
                    this.clabe = null;
                    const res = await axios.post('/api/v1/bank-accounts/user-info', {
                        user_type: this.userType,
                        user_id: this.userId
                    });

                    if (res.data) {
                        this.userInfo = res.data;
                        this.userBankAccounts = res.data.bank_accounts;
                        this.hasProfile = true;
                        this.isMounted = true;
                    }
                    this.successNotif("Información del usuario consultada correctamente.");
                    this.showLoading(false);
                } catch (error) {
                    console.error(error);
                    this.userBankAccounts = [];
                    this.hasProfile = false;
                    this.userInfo = null;
                    this.isMounted = false;
                    this.missingFieldsNotif();
                    this.errorMessage = error.response.data.error || 'Ocurrió un error al consultar la información del usuario.';
                } finally {
                    this.showLoading(false);
                }
            },
            async registerBankAccount() {
                this.errorMessageClabe = null;
                if (!this.clabe || this.clabe.length !== 18) {
                    this.errorMessageClabe = "La CLABE debe tener 18 dígitos.";
                    return;
                }

                this.showLoading(true, "Guardando información...");

                try {
                    let payload = {
                        clabe: this.clabe,
                        is_bo_action: true,
                        user_id: this.userInfo.user_id,
                    };

                    const res = await axios.post('/api/app/bank-account/bo/national', payload);

                    this.fetchUserBankAccounts();
                    this.showLoading(false);

                } catch (e) {
                    this.showLoading(false);
                    this.errorMessageClabe = e.response.data.message || 'Ocurrió un error al registrar la cuenta bancaria.';
                } finally {
                    this.showLoading(false);
                }
            },
            isDefaultAccount(bankAccount) {
                return bankAccount.is_default === 1 || bankAccount.is_default === true;
            },
            isVerifiedAccount(bankAccount) {
                return bankAccount.verified === BankAccountStatus.VERIFIED;
            },
            canBeSetAsDefault(bankAccount) {
                return this.isVerifiedAccount(bankAccount) && !this.isDefaultAccount(bankAccount);
            },
            getDefaultAccountIconClass(bankAccount) {
                if (this.isDefaultAccount(bankAccount)) {
                    return `${this.klassBase} fill-current`;
                }

                return this.canBeSetAsDefault(bankAccount) ? this.klassFull : `${this.klassBase} opacity-30`;
            },
            setAsDefaultRequest(bankAccount) {
                if (this.isDefaultAccount(bankAccount)) {
                    return;
                }

                if (!this.isVerifiedAccount(bankAccount)) {
                    this.warningNotifDialog(
                        "Cuenta no verificada",
                        "No es posible marcar esta cuenta como favorita porque aún no ha sido verificada."
                    );
                    return;
                }

                this.accountIdToSetAsDefault = bankAccount.id;
                this.confirmationDialog(
                    this.doSetAsDefault,
                    "Marcar cuenta como favorita",
                    `Confirma que deseas marcar la cuenta ${bankAccount.clabe} como la cuenta favorita del perfil.`,
                    "Confirmar"
                );
            },
            async doSetAsDefault() {
                if (this.accountIdToSetAsDefault === null || this.userInfo === null) {
                    return;
                }

                const bankAccountId = this.accountIdToSetAsDefault;
                this.showLoading(true, "Actualizando cuenta favorita...");

                try {
                    await axios.post(`/api/backoffice/bank-account/${this.userInfo.user_id}/set-default`, {
                        bank_account_id: bankAccountId
                    });

                    this.userBankAccounts = this.userBankAccounts.map(account => ({
                        ...account,
                        is_default: account.id === bankAccountId ? 1 : 0
                    }));

                    this.successNotif("Preferencias actualizadas", "La cuenta favorita del perfil ha sido actualizada.");
                } catch (e) {
                    console.error(e);
                    this.errorNotif(
                        "Error al actualizar la cuenta favorita",
                        e.response?.data?.message || 'Ocurrió un error al marcar la cuenta como favorita.'
                    );
                } finally {
                    this.accountIdToSetAsDefault = null;
                    this.showLoading(false);
                }
            },
        }

    }
</script>